import { useEffect, useMemo, useState } from "react";

import { ApiError, apiClient } from "../../api/client";
import type { AnonymityState, GumballsMeResponse, LedgerEntry } from "../../api/types";
import type { MeResponse } from "../../api/types";
import { hasPermission } from "../../auth/permissions";
import { displayName } from "../../lib/format";
import { useTheme } from "../../themes/ThemeProvider";
import type { ReceivedGumball } from "../../themes/types";
import { AnonymityChoiceModal } from "./AnonymityChoiceModal";
import { BankPanel } from "./BankPanel";
import { GiveModal } from "./GiveModal";
import { GumballJars } from "./GumballJars";
import { HistoryFeed } from "./HistoryFeed";
import { NotesPanel } from "./NotesPanel";
import {
  type ReceivedGumballDetail,
  ReceivedGumballsModal,
} from "./ReceivedGumballsModal";
import { TutorialModal } from "./TutorialModal";

// gumballSourceLabel returns the display label for a `gumball_spend` entry's
// sender. The backend masks anon entries by setting source_user_id to null,
// so a spend with a null source is always a masked anon entry.
export function gumballSourceLabel(entry: LedgerEntry): string {
  if (entry.entry_type === "gumball_spend" && entry.source_user_id === null) {
    return "Anonymous";
  }
  return displayName(null, entry.source_user_email);
}

interface PageState {
  status: "loading" | "ready" | "error";
  meGumballs: GumballsMeResponse | null;
  ledger: LedgerEntry[];
  errorMessage: string | null;
}

const initialState: PageState = {
  status: "loading",
  meGumballs: null,
  ledger: [],
  errorMessage: null
};

export function GumballsPage({ me }: { me: MeResponse }) {
  const { theme } = useTheme();
  const [state, setState] = useState<PageState>(initialState);
  const [giveOpen, setGiveOpen] = useState(false);
  const [receivedOpen, setReceivedOpen] = useState(false);
  const [tutorialDismissed, setTutorialDismissed] = useState(false);
  const canGive = hasPermission(me, "gumballs:give");

  // Every role gets the same gumballs UI; the only role-specific limit is
  // that the recipient list returned by /api/gumballs/recipients is always
  // active ICs only (enforced server-side). When there's no active season we
  // still show a calm "Season hasn't started yet" empty state.

  useEffect(() => {
    let cancelled = false;
    setState({ ...initialState });

    async function load() {
      try {
        const meGumballs = await apiClient.getGumballsMe();
        if (cancelled) {
          return;
        }
        if (meGumballs.season === null) {
          setState({
            status: "ready",
            meGumballs,
            ledger: [],
            errorMessage: null
          });
          return;
        }
        // Skip the ledger fetch when the user must still pick an anonymity
        // preference — the API would 409 with anonymity_choice_required and
        // the modal blocks the page anyway.
        if (meGumballs.anonymity?.required === true) {
          setState({
            status: "ready",
            meGumballs,
            ledger: [],
            errorMessage: null
          });
          return;
        }
        const ledger = await apiClient.listLedger({
          seasonID: meGumballs.season.id,
          userID: me.user.id,
          limit: 100
        });
        if (cancelled) {
          return;
        }
        setState({
          status: "ready",
          meGumballs,
          ledger: ledger.entries,
          errorMessage: null
        });
      } catch (caught) {
        if (cancelled) {
          return;
        }
        setState({
          status: "error",
          meGumballs: null,
          ledger: [],
          errorMessage: messageFor(caught)
        });
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [me.user.id]);

  function handleAnonymityChosen(state: AnonymityState) {
    setState((previous) => {
      if (previous.meGumballs === null) {
        return previous;
      }
      return {
        ...previous,
        meGumballs: { ...previous.meGumballs, anonymity: state }
      };
    });
    // Re-fetch the page once the gate clears so the ledger and balance
    // reflect the choice.
    void reloadAfterChoice(me.user.id, setState);
  }

  const coinsFromLeaders = useMemo(() => {
    // Every leadership-driven credit counts: CSV grants (coin_grant), one-off
    // admin nudges (manual_adjustment), and coins kept from a prior season
    // (season_carryover). Summing all three makes the stat compose cleanly
    // with "coins left to spend" — no more "0 from leaders, 50 to spend"
    // mismatches.
    return state.ledger
      .filter(
        (entry) =>
          (entry.entry_type === "coin_grant" ||
            entry.entry_type === "manual_adjustment" ||
            entry.entry_type === "season_carryover") &&
          entry.target_user_id === me.user.id
      )
      .reduce((total, entry) => total + entry.amount, 0);
  }, [state.ledger, me.user.id]);

  const receivedGumballs = useMemo<ReceivedGumball[]>(() => {
    return state.ledger
      .filter((entry) => entry.entry_type === "gumball_spend" && entry.target_user_id === me.user.id)
      .map((entry) => ({
        id: entry.id,
        from: gumballSourceLabel(entry),
        amount: entry.amount,
        reason: entry.reason
      }));
  }, [state.ledger, me.user.id]);

  const receivedDetails = useMemo<ReceivedGumballDetail[]>(() => {
    return receivedLedgerDetails(state.ledger, me.user.id);
  }, [state.ledger, me.user.id]);

  if (state.status === "loading") {
    return (
      <div className="page page--gumballs">
        <p className="loading loading--quiet">Loading…</p>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="page page--gumballs">
        <div className="card">
          <h2>We couldn't reach the gumball factory.</h2>
          <p>{state.errorMessage}</p>
        </div>
      </div>
    );
  }

  const meGumballs = state.meGumballs;

  const Scene = theme.assets.Scene;

  return (
    <div className="page page--gumballs">
      <div className="page--gumballs__stage">
        <div className="page--gumballs__scene" aria-hidden="true">
          {Scene !== undefined ? (
            <Scene received={receivedGumballs} />
          ) : (
            theme.assets.decorations.map((decoration) => (
              <div key={decoration.id} className={`scene__layer scene__layer--${decoration.id}`}>
                {decoration.node}
              </div>
            ))
          )}
        </div>
        <div className="page--gumballs__primary">
          {meGumballs?.season === null && <NoActiveSeasonEmptyState />}
          {meGumballs !== null && meGumballs.season !== null && (
            <GumballJars
              availableCoins={meGumballs.balance.available_coins}
              receivedGumballs={meGumballs.balance.gumballs_received}
              onReceivedClick={() => setReceivedOpen(true)}
            />
          )}
        </div>
      </div>
      <aside className="gumballs-sidebar" aria-label="Gumball bank and season activity">
        <BankPanel
          season={meGumballs?.season ?? null}
          balance={meGumballs?.balance ?? { available_coins: 0, gumballs_received: 0, gumballs_given: 0 }}
          coinsFromLeaders={coinsFromLeaders}
          canGive={canGive}
          onGive={() => setGiveOpen(true)}
        />
        {meGumballs !== null && meGumballs.season !== null && (
          <HistoryFeed entries={state.ledger} meUserID={me.user.id} loading={false} />
        )}
        <NotesPanel me={me} />
      </aside>
      {giveOpen && meGumballs?.season !== null && meGumballs !== null && (
        <GiveModal
          open={giveOpen}
          availableCoins={meGumballs.balance.available_coins}
          seasonID={meGumballs.season.id}
          isAnonymous={meGumballs.anonymity?.anonymous === true}
          onClose={() => setGiveOpen(false)}
          onSent={(entry) => {
            setGiveOpen(false);
            setState((previous) => {
              if (previous.meGumballs === null) {
                return previous;
              }
              return {
                ...previous,
                ledger: [entry, ...previous.ledger],
                meGumballs: {
                  ...previous.meGumballs,
                  balance: {
                    ...previous.meGumballs.balance,
                    available_coins: previous.meGumballs.balance.available_coins - entry.amount,
                    gumballs_given: previous.meGumballs.balance.gumballs_given + entry.amount
                  }
                }
              };
            });
          }}
        />
      )}
      {meGumballs !== null && (
        <ReceivedGumballsModal
          open={receivedOpen}
          entries={receivedDetails}
          total={meGumballs.balance.gumballs_received}
          onClose={() => setReceivedOpen(false)}
        />
      )}
      {meGumballs?.season !== null
        && meGumballs?.anonymity?.required === true
        && meGumballs !== null
        && !tutorialDismissed
        && (
          <TutorialModal open onDismiss={() => setTutorialDismissed(true)} />
        )}
      {meGumballs?.season !== null
        && meGumballs?.anonymity?.required === true
        && meGumballs !== null
        && tutorialDismissed
        && (
          <AnonymityChoiceModal
            open
            seasonID={meGumballs.season.id}
            seasonName={meGumballs.season.name}
            onChosen={handleAnonymityChosen}
          />
        )}
    </div>
  );
}

function receivedLedgerDetails(
  entries: LedgerEntry[],
  meUserID: string
): ReceivedGumballDetail[] {
  return entries
    .filter((entry) => entry.entry_type === "gumball_spend" && entry.target_user_id === meUserID)
    .map((entry) => ({
      id: entry.id,
      giver: gumballSourceLabel(entry),
      amount: entry.amount,
      reason: entry.reason?.trim() === "" ? null : entry.reason,
      createdAt: entry.created_at
    }))
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
}

async function reloadAfterChoice(
  userID: string,
  setState: React.Dispatch<React.SetStateAction<PageState>>
): Promise<void> {
  try {
    const meGumballs = await apiClient.getGumballsMe();
    if (meGumballs.season === null) {
      setState({ status: "ready", meGumballs, ledger: [], errorMessage: null });
      return;
    }
    const ledger = await apiClient.listLedger({
      seasonID: meGumballs.season.id,
      userID,
      limit: 100
    });
    setState({ status: "ready", meGumballs, ledger: ledger.entries, errorMessage: null });
  } catch (caught) {
    setState({ status: "error", meGumballs: null, ledger: [], errorMessage: messageFor(caught) });
  }
}

function NoActiveSeasonEmptyState() {
  return (
    <section className="card empty-state">
      <h2>The season hasn't started yet.</h2>
      <p>Once leadership opens a season and grants coins, you'll see your bank fill up here.</p>
    </section>
  );
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    if (caught.status === 503) {
      return "Service is temporarily unavailable — please retry.";
    }
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Could not load gumballs data.";
}
